import React, { useContext, useMemo } from 'react';
import moment from 'moment';


/////////////////////////////////////////////////////////////////////////////
// Shipment Data
/////////////////////////////////////////////////////////////////////////////
const ShipmentDataContext = React.createContext({});
export const ShipmentDataProvider = ShipmentDataContext.Provider;
export function useShipmentData() {
  const {
    confirmation_requirement,
    signature_required,
    currency_code,
    ...shipmentData
  } = useContext(ShipmentDataContext);

  // confirmation_requirement = oneOf( null, 'signature', 'adult' )

  return {
    ...shipmentData,
    confirmation_requirement: confirmation_requirement || (signature_required && 'signature'),
    currency_code: currency_code || 'USD'
  }
}


/////////////////////////////////////////////////////////////////////////////
// Branding Data
/////////////////////////////////////////////////////////////////////////////
const DEFAULT_TOGGLES = {
  show_logo: true,
  show_custom_colors: true,
  show_social_media: true,
  show_store_url: true,
  show_menu_links: true,
  show_return_policy: true,
  show_phone: true,
  show_email: true,
  show_shipment_details: true,
  show_item_images: true,
  show_item_options: true,
  show_shipment_prices: true
};


const BrandingDataContext = React.createContext({});
export const BrandingDataProvider = BrandingDataContext.Provider;
export function useBrandingData() {
  const brandingData = useContext(BrandingDataContext);
  const toggles = brandingData.toggles || DEFAULT_TOGGLES;
  const colors = toggles.show_custom_colors ? (brandingData.colors || {}) : {};

  return {
    ...brandingData,
    toggles,
    colors
  };
}


/////////////////////////////////////////////////////////////////////////////
// Tracking Data
/////////////////////////////////////////////////////////////////////////////
export const useOrderedCarrierEvents = (carrier_events) => {

  // Note: Assumes that events are already sorted by occurrence but we don't
  // know which direction. Finds the first and last event w/ valid timestamps
  // and compares to keep the order or reverse. This keep any events w/
  // invalid timestamps in place.
  return useMemo( () => {
    let orderedEvents = carrier_events || [];

    if( orderedEvents.length > 1 ) {
      const reversedEvents = [...orderedEvents].reverse();
      const firstTimestampedEvent = orderedEvents.find( e => e.occurred_at && moment(e.occurred_at).isValid() );
      const lastTimestampedEvent = reversedEvents.find( e => e.occurred_at && moment(e.occurred_at).isValid() );

      if( moment(firstTimestampedEvent.occurred_at).isBefore(moment(lastTimestampedEvent.occurred_at)) ) {
        orderedEvents = reversedEvents;
      }
    }

    return orderedEvents;
  }, [carrier_events] );
}

const TrackingDataContext = React.createContext({});
export const TrackingDataProvider = TrackingDataContext.Provider;
export function useTrackingData() {
  const trackingData = useContext(TrackingDataContext) || {};
  const carrier_events = useOrderedCarrierEvents(trackingData.carrier_events);

  return {
    ...trackingData,
    carrier_events,
    isDelivered: (trackingData.source_status_code || '').toUpperCase() === 'DE',
    latestUpdate: (carrier_events && carrier_events[0]),
    sourceEvent: (carrier_events && carrier_events[carrier_events.length-1]),
  };
}


/////////////////////////////////////////////////////////////////////////////
// Preview Config
/////////////////////////////////////////////////////////////////////////////
const PreviewConfigContext = React.createContext({});
export const PreviewConfigProvider = PreviewConfigContext.Provider;
export function usePreviewConfig() {
  return useContext(PreviewConfigContext) || {};
}